import { TenantInvoice, TenantsParkingData } from 'types';
import { ALL_TIME_MONTH_YEAR } from 'utils/monthYear';

export function parseMonthYearValue(value: string): { month?: number; year?: number } {
    if (!value || value === ALL_TIME_MONTH_YEAR) return {};
    const [month, year] = value.split(':').map(Number);
    return { month, year };
}

export type TenantSummary = {
    uuid: string;
    name: string;
};

// Until the page has its own tenant list endpoint, derive tenants from the spot's bulk rentals.
export function tenantsFromBulkRentals(bulkRentals: TenantsParkingData[]): TenantSummary[] {
    const tenants = new Map<string, TenantSummary>();
    bulkRentals.forEach(({ tenant_uuid, description }) => {
        if (tenant_uuid && !tenants.has(tenant_uuid)) {
            tenants.set(tenant_uuid, { uuid: tenant_uuid, name: description || 'Unnamed tenant' });
        }
    });
    return Array.from(tenants.values());
}

export type InvoiceDisplayStatus = 'overdue' | 'paid' | 'open' | 'other';

const OPEN_INVOICE_STATUSES = ['open', 'finalized', 'uncollectible'];

export function deriveInvoiceDisplayStatus(invoice: TenantInvoice): InvoiceDisplayStatus {
    if (invoice.is_overdue) return 'overdue';
    if (invoice.status === 'paid') return 'paid';
    if (invoice.status && OPEN_INVOICE_STATUSES.includes(invoice.status)) return 'open';
    return 'other';
}

export function getInvoiceAction(invoice: TenantInvoice): { url: string; action: 'download' | 'view' } | null {
    const displayStatus = deriveInvoiceDisplayStatus(invoice);
    if (displayStatus === 'paid' && invoice.invoice_pdf) {
        return { url: invoice.invoice_pdf, action: 'download' };
    }
    if ((displayStatus === 'open' || displayStatus === 'overdue') && invoice.hosted_invoice_url) {
        return { url: invoice.hosted_invoice_url, action: 'view' };
    }
    return null;
}

const INVOICE_STATUS_LABEL: Record<Exclude<InvoiceDisplayStatus, 'other'>, string> = {
    overdue: 'Overdue',
    paid: 'Paid',
    open: 'Open',
};

function capitalize(value: string): string {
    return value.charAt(0).toUpperCase() + value.slice(1);
}

export function invoiceStatusLabel(invoice: TenantInvoice): string {
    const displayStatus = deriveInvoiceDisplayStatus(invoice);
    if (displayStatus !== 'other') return INVOICE_STATUS_LABEL[displayStatus];
    return invoice.status ? capitalize(invoice.status) : '—';
}

export function formatInvoiceDate(epoch: string | null, timeZone?: string | null): string {
    if (!epoch) return '—';
    return new Date(Number(epoch) * 1000).toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
        timeZone: timeZone || undefined,
    });
}
